// import node_modules
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Heading, Spacer, Text } from '@storylinerlabs/design';
import { useTranslation } from 'react-i18next';

// import types
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import {
  ReleaseNoteAppType,
  ReleaseNoteAppTypeEnum,
  ReleaseNoteCategory,
  ReleaseNoteCategoryEnum,
} from '../../types';

// import component
import Column from '../atoms/Column';
import EarlyAccessSection from '../elements/EarlyAccessSection';
import MainLayout from '../layouts/MainLayout';
import ProductUpdatesForm from '../forms/ProductUpdatesForm';
import ReleaseNotesList from '../lists/ReleaseNotesList';
import SEO from '../meta/SEO';
import Section from '../atoms/Section';

// define page
const ReleaseNotesTemplate: React.FunctionComponent<{
  hrefLang: string;
  releaseNotes: {
    app: ReleaseNoteAppType;
    category: ReleaseNoteCategory;
    content: MDXRemoteSerializeResult;
    ctaLink?: string | null;
    ctaText?: string | null;
    pubDate: string;
    title: string;
    type: string;
    slug: string;
  }[];
}> = ({ hrefLang, releaseNotes }) => {
  // init hooks
  const { t } = useTranslation('release-notes');

  // init render
  return (
    <>
      <SEO
        description={t('seo.description')}
        hrefLang={hrefLang}
        image={t('seo.image')}
        title={t('seo.title')}
      />

      <MainLayout>
        {/* Hero  */}
        <Section bgColor="desertStorm">
          {/* Navbar */}
          <Spacer height="64px" />
          <Spacer mobileHeight="25px" tabletHeight="25px" height="75px" />
        </Section>
        <Section bgColor="desertStorm">
          <Column cols="md">
            <Heading alignment="center" tag="h1">
              {t('hero.title')}
            </Heading>
            <Text alignment="center" size="large">
              {t('hero.intro')}
            </Text>

            <ProductUpdatesForm />
          </Column>
        </Section>
        {/* TODO: implement newsletter and twitter subscription */}
        <Section bgColor="desertStorm">
          <Spacer mobileHeight="25px" tabletHeight="25px" height="75px" />
        </Section>

        {/* Release notes */}
        <ReleaseNotesList releaseNotes={releaseNotes} />

        {/* Call to action  */}
        <Section bgColor="anakiwa">
          <Spacer mobileHeight="25px" tabletHeight="50px" height="75px" />
        </Section>
        <EarlyAccessSection bgColor="anakiwa" />
        <Section bgColor="anakiwa">
          <Spacer mobileHeight="25px" tabletHeight="50px" height="75px" />
        </Section>
        <Section>
          <Spacer height="50px" />
        </Section>
      </MainLayout>
    </>
  );
};

ReleaseNotesTemplate.propTypes = {
  hrefLang: PropTypes.string.isRequired,
  releaseNotes: PropTypes.arrayOf(
    PropTypes.shape({
      app: PropTypes.oneOf(Object.values(ReleaseNoteAppTypeEnum)).isRequired,
      category: PropTypes.oneOf(Object.values(ReleaseNoteCategoryEnum)).isRequired,
      content: PropTypes.shape({ compiledSource: PropTypes.string.isRequired }).isRequired,
      ctaLink: PropTypes.string,
      ctaText: PropTypes.string,
      pubDate: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
};

export default ReleaseNotesTemplate;
